/**
 * Уніфіковані дії друкованої форми (рішення власника 2026-08-13):
 * «друкувати» + «назад» + «зберегти в Excel» (ч.1303) + «підписати і зберегти» — знімок форми
 * (html2canvas по .print-page) відкривається у редакторі печаток/підписів
 * (той самий, що у вкладеннях): накласти печатку/підпис організації або
 * власний підпис → зберегти ВКЛАДЕННЯМ до документа → звідти ж друк.
 *
 * docMeta відсутній (звіт без документа) → лише друкувати/назад.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { attachments, type Attachment } from '@/api/attachments';
import { getMe } from '@/api/auth';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import SaveXlsxButton from '@/shared/components/SaveXlsxButton';

export interface PrintDocMeta {
  organizationId: string;
  documentType: string;
  documentId: string;
  /** Базове імʼя файла для збереження (напр. «Акт списання АС-1»). */
  filename: string;
  /** Email контрагента для передзаповнення (з картки), опц. */
  contractorEmail?: string | null;
  /** Назва організації-відправника (для «Від: <org> через Облік+»). */
  orgName?: string | null;
}

/** Поріг «масивної» форми для підписання знімком (рішення 2026-08-24). */
export const MAX_SIGN_PAGES = 10;

interface Overlay {
  kind: string; attId: string; url: string;
  x: number; y: number; w: number;
}

export default function PrintActions({ docMeta, xlsxBuild }: {
  docMeta?: PrintDocMeta | null;
  /** ч.1373: власна книга Excel форми (рахунок за зразком 1С) замість розбору розмітки. */
  xlsxBuild?: () => Promise<{ blob: Blob; filename: string }>;
}) {
  const qc = useQueryClient();
  const [shot, setShot] = useState<string | null>(null);   // dataURL знімка
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  // Прогрес побудови PDF («стор. X/Y») — довгі форми будуються секунди.
  const [pdfProgress, setPdfProgress] = useState<string | null>(null);
  // Оцінка к-сті A4-сторінок форми (ResizeObserver — рядки довантажуються
  // асинхронно). Понад MAX_SIGN_PAGES «підписати і зберегти» недоступно:
  // знімок такої простині = хвилини побудови + падіння якості + ліміт 10 МБ;
  // правильний шлях — «друкувати» → «Зберегти як PDF» (вектор, миттєво).
  const [pageEstimate, setPageEstimate] = useState<number | null>(null);
  useEffect(() => {
    const el = document.querySelector('.print-page') as HTMLElement | null;
    if (!el) return;
    const measure = () => {
      const w = el.scrollWidth || 1;
      const landscape = w > 950;
      const pageCssH = Math.floor(w * ((landscape ? 210 : 297) / (landscape ? 297 : 210)));
      setPageEstimate(Math.max(1, Math.ceil(el.scrollHeight / pageCssH)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const tooBig = (pageEstimate ?? 0) > MAX_SIGN_PAGES;
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ idx: number; dx: number; dy: number } | null>(null);

  const orgId = docMeta?.organizationId ?? '';
  const permCompany = usePermissionsByOrg('documents.stamps_company.use');
  const permDocs = usePermissionsByOrg('documents.stamps_docs.use');
  const canCompany = permCompany.availableOrgs.some((o) => o.id === orgId);
  const canDocs = permDocs.availableOrgs.some((o) => o.id === orgId);
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: !!shot });

  const mkQ = (type: string, docId: string | undefined) => ({
    queryKey: ['attachments', type, docId, orgId],
    queryFn: () => attachments.list({
      organization_id: orgId, document_type: type, document_id: docId! }),
    enabled: !!orgId && !!shot && !!docId,
  });
  const stampQ = useQuery(mkQ('org.stamp', orgId));
  const stampDocsQ = useQuery(mkQ('org.stamp_docs', orgId));
  const signQ = useQuery(mkQ('org.signature', orgId));
  const userSignQ = useQuery(mkQ('user.signature', meQ.data?.id));

  const stampButtons: { kind: string; label: string; list?: Attachment[] }[] = [
    ...(canCompany ? [
      { kind: 'org.stamp', label: '+ печатка підприємства', list: stampQ.data },
      { kind: 'org.signature', label: '+ підпис керівника', list: signQ.data },
    ] : []),
    ...(canDocs ? [
      { kind: 'org.stamp_docs', label: '+ штамп для документів', list: stampDocsQ.data },
      { kind: 'user.signature', label: '+ мій підпис', list: userSignQ.data },
    ] : []),
  ].filter((b) => (b.list?.length ?? 0) > 0);

  const capture = async () => {
    setCapturing(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const el = document.querySelector('.print-page') as HTMLElement;
      await document.fonts?.ready;
      // Широкі (альбомні) форми, як довідка переоцінки валюти: без явних
      // width/windowWidth html2canvas ріже контент за межами viewport —
      // знімок у редакторі печаток виходив порожнім/обрізаним.
      // ДУЖЕ ДОВГІ форми (довідка за роки, сотні рядків): canvas у браузера
      // обмежений ~32k px по стороні — при scale 2 знімок виходив битим
      // (порожня картинка, збереження падало). Масштаб обмежуємо лімітом.
      const MAX_DIM = 16000;
      const scale = Math.min(
        2, MAX_DIM / Math.max(1, el.scrollWidth), MAX_DIM / Math.max(1, el.scrollHeight));
      const canvas = await html2canvas(el, {
        scale, backgroundColor: '#ffffff',
        width: el.scrollWidth, height: el.scrollHeight,
        ignoreElements: (n) => !!n.classList?.contains('print-hide'),
      });
      if (!canvas.width || !canvas.height) {
        throw new Error('empty canvas');
      }
      const url = canvas.toDataURL('image/jpeg', 0.9);
      if (url.length < 100) {
        throw new Error('broken snapshot');
      }
      setShot(url);
      setOverlays([]);
    } catch {
      window.alert('Не вдалося зробити знімок форми — спробуйте ще раз');
    } finally {
      setCapturing(false);
    }
  };

  const addOverlay = async (kind: string, list?: Attachment[]) => {
    const att = list?.[list.length - 1];
    if (!att) return;
    // Один штамп кожного виду на форму (повторний клік = дубль печатки).
    if (overlays.some((o) => o.kind === kind)) return;
    const blob = await attachments.downloadBlob(att.id);
    setOverlays((o) => [...o, {
      kind, attId: att.id, url: URL.createObjectURL(blob),
      x: 40 + o.length * 30, y: 120 + o.length * 30, w: 140,
    }]);
  };

  const onDragStart = (idx: number, e: React.PointerEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragRef.current = { idx, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const host = imgRef.current?.parentElement;
    if (!host) return;
    const r = host.getBoundingClientRect();
    setOverlays((o) => o.map((ov, i) => i === d.idx
      ? { ...ov, x: Math.max(0, e.clientX - r.left - d.dx),
          y: Math.max(0, e.clientY - r.top - d.dy) }
      : ov));
  };
  const onDragEnd = () => { dragRef.current = null; };

  /**
   * Якісний постраничний PDF (A4) — рішення власника 2026-08-24: вкладення
   * мають друкуватись без втрати якості. Форма знімається ПОСЕГМЕНТНО у
   * масштабі 2 (кожна A4-сторінка — свій canvas, без глобального
   * canvas-ліміту і без даунскейлу довгих форм), печатки/підписи
   * вжигаються у повній роздільності, сторінки йдуть у PDF як PNG
   * (без JPEG-артефактів на тексті).
   */
  const composePdf = async () => {
    const { default: html2canvas } = await import('html2canvas');
    const { jsPDF } = await import('jspdf');
    const el = document.querySelector('.print-page') as HTMLElement;
    await document.fonts?.ready;
    const cssW = el.scrollWidth;
    const cssH = el.scrollHeight;
    // Портрет для звичайних форм (~794px = 210mm), ландшафт для широких
    // (довідка переоцінки ~1100px = 290mm).
    const landscape = cssW > 950;
    const [pageWmm, pageHmm] = landscape ? [297, 210] : [210, 297];
    const pageCssH = Math.floor(cssW * (pageHmm / pageWmm));
    const pages = Math.max(1, Math.ceil(cssH / pageCssH));
    // ОДИН знімок усієї форми + нарізка на сторінки через drawImage.
    // Посегментна зйомка (opts.x/y html2canvas) давала зсунутий/обрізаний
    // PDF — двічі (2026-08-24). Повний знімок — той самий шлях, що працював
    // у JPG-версії, тому нарізаємо вже ГОТОВИЙ canvas, а не DOM.
    const MAX_DIM = 16384;          // ліміт сторони canvas у браузерах
    const MAX_AREA = 200_000_000;   // запас під area-ліміт (Chrome ~268M)
    const scale = Math.min(
      2, MAX_DIM / cssW, MAX_DIM / cssH, Math.sqrt(MAX_AREA / (cssW * cssH)));
    setPdfProgress('знімок…');
    const full = await html2canvas(el, {
      scale, backgroundColor: '#ffffff',
      width: cssW, height: cssH,
      ignoreElements: (n) => !!n.classList?.contains('print-hide'),
    });
    if (!full.width || !full.height) throw new Error('empty canvas');
    const f = full.width / cssW;
    // Печатки/підписи вжигаємо у повній роздільності знімка. Позиції — у
    // CSS-px форми (редактор показує знімок, стиснутий до ширини модалки).
    const img = imgRef.current;
    const dispScale = img ? cssW / img.clientWidth : 1;
    const fctx = full.getContext('2d')!;
    // html2canvas ЛИШАЄ на контексті свою матрицю (заміряно Playwright:
    // scale(2) + translate(-373, 0)) — без скидання печатка малювалась за
    // подвоєними і зсунутими координатами й «виїжджала» за лівий край
    // (прецедент 2026-08-24). У JPG-версії багу не було, бо композиція
    // йшла у НОВИЙ canvas. Скидаємо матрицю ДО малювання штампів.
    fctx.setTransform(1, 0, 0, 1, 0, 0);
    for (const ov of overlays) {
      const im = new Image();
      im.src = ov.url;
      await im.decode();
      const w = ov.w * dispScale;
      const h = w * (im.naturalHeight / im.naturalWidth);
      fctx.drawImage(im, ov.x * dispScale * f, ov.y * dispScale * f, w * f, h * f);
    }
    const pdf = new jsPDF({
      orientation: landscape ? 'landscape' : 'portrait',
      unit: 'mm', format: 'a4', compress: true,
    });
    // PNG у jsPDF парситься чистим JS — заміряно (Playwright, 2026-08-24):
    // 1 стор. PNG 0.8 с, 3 стор. PNG 4.7 с, а 9 стор. JPEG 0.92 — 0.9 с
    // при 161 dpi / 3 МБ. Тому PNG лише для 1–2 сторінок (типова первинка),
    // далі JPEG. Ліміт вкладень 10 МБ перевіряється у saveToDoc.
    const useJpeg = pages > 2;
    for (let i = 0; i < pages; i++) {
      const segCssH = Math.min(pageCssH, cssH - i * pageCssH);
      if (segCssH <= 0) break;
      const page = document.createElement('canvas');
      page.width = full.width;
      page.height = Math.max(1, Math.round(segCssH * f));
      page.getContext('2d')!.drawImage(
        full, 0, Math.round(i * pageCssH * f), full.width, page.height,
        0, 0, page.width, page.height);
      if (i > 0) pdf.addPage();
      pdf.addImage(
        useJpeg ? page.toDataURL('image/jpeg', 0.92) : page.toDataURL('image/png'),
        useJpeg ? 'JPEG' : 'PNG',
        0, 0, pageWmm, (segCssH / cssW) * pageWmm);
      setPdfProgress(`стор. ${i + 1}/${pages}`);
      await new Promise((r) => setTimeout(r, 0));
    }
    setPdfProgress(null);
    return pdf;
  };

  const saveToDoc = async () => {
    if (!docMeta) return;
    setSaving(true);
    try {
      const pdf = await composePdf();
      const blob = pdf.output('blob');
      if (blob.size > 10 * 1024 * 1024) {
        window.alert(`PDF завеликий: ${(blob.size / 1024 / 1024).toFixed(1)} МБ `
          + '(ліміт вкладень 10 МБ). Зменшіть період довідки або збережіть '
          + 'через друк браузера («друкувати» → «Зберегти як PDF»).');
        return;
      }
      const suffix = overlays.length > 0 ? ' (підписано)' : '';
      const file = new File([blob], `${docMeta.filename}${suffix}.pdf`,
        { type: 'application/pdf' });
      await attachments.upload({
        organization_id: docMeta.organizationId,
        document_type: docMeta.documentType,
        document_id: docMeta.documentId,
        file,
      });
      qc.invalidateQueries({
        queryKey: ['attachments', docMeta.documentType, docMeta.documentId] });
      window.alert('Збережено у вкладення документа (PDF)');
    } catch {
      window.alert('Не вдалося зберегти у вкладення');
    } finally {
      setSaving(false);
      setPdfProgress(null);
    }
  };

  const printShot = async () => {
    if (pdfProgress) return;
    try {
      setPdfProgress('підготовка…');
      const pdf = await composePdf();
      const url = URL.createObjectURL(pdf.output('blob'));
      window.open(url, '_blank');
    } catch {
      window.alert('Не вдалося сформувати PDF для друку');
    } finally {
      setPdfProgress(null);
    }
  };

  return (
    <>
      <div className="actions print-hide" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button onClick={() => window.print()} className="primary">друкувати</button>
        <button onClick={() => window.history.back()}>назад</button>
        {/* ч.1303 (рішення власника): та сама форма файлом Excel. */}
        <SaveXlsxButton filename={docMeta?.filename} build={xlsxBuild} />
        {docMeta && (
          <button onClick={() => void capture()} disabled={capturing || tooBig}
            title={tooBig
              ? `Форма завелика для підписання знімком (${pageEstimate} стор., ліміт ${MAX_SIGN_PAGES})`
              : 'Зробити знімок форми, накласти печатку/підпис і зберегти у вкладення документа'}>
            {capturing ? 'знімок…' : 'підписати і зберегти'}
          </button>
        )}
        {docMeta && tooBig && (
          <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#b45309',
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4,
            padding: '2px 8px' }}>
            ⚠ Форма масивна ({pageEstimate} стор. A4, ліміт для підписання —
            {' '}{MAX_SIGN_PAGES}): збережіть через «друкувати» → «Зберегти як PDF»
          </span>
        )}

      </div>

      {shot && (
        // print-hide ОБОВʼЯЗКОВО: PrintActions рендериться ВСЕРЕДИНІ
        // .print-page, а модалка — position:fixed на весь екран. Без цього
        // класу html2canvas знімає модалку РАЗОМ з документом: у PDF
        // зʼявлялась сіра смуга + зсув (модалка ширша за сторінку) і «дубль
        // печатки» (у модалці ж превʼю документа зі штампом). Прецедент
        // 2026-08-24: у JPG-версії знімок робився ДО відкриття модалки.
        <div className="print-hide" style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShot(null)}>
          <div style={{
            background: '#fff', borderRadius: 8, width: 'min(920px, 94vw)',
            height: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.6rem 0.9rem', borderBottom: '1px solid #e2e8f0',
              flexWrap: 'wrap',
            }}>
              <strong style={{ flex: 1, minWidth: 120 }}>Підписання форми</strong>
              {stampButtons.map((b) => (
                <button key={b.kind} onClick={() => void addOverlay(b.kind, b.list)}
                  title="Перетягніть штамп мишею на потрібне місце">
                  {b.label}
                </button>
              ))}
              <button className="primary" disabled={saving || !!pdfProgress}
                onClick={() => void saveToDoc()}
                title="Зберегти знімок з печатками у вкладення документа">
                {saving ? (pdfProgress ?? 'збереження…') : 'зберегти у вкладення'}
              </button>
              <button onClick={() => void printShot()} disabled={saving || !!pdfProgress}>
                {!saving && pdfProgress ? pdfProgress : 'друкувати'}
              </button>
              <button onClick={() => setShot(null)} title="Закрити">✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', background: '#f1f5f9',
              borderRadius: '0 0 8px 8px', display: 'flex', justifyContent: 'center' }}
              onPointerMove={onDragMove} onPointerUp={onDragEnd}>
              <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
                <img ref={imgRef} src={shot} alt="знімок форми"
                  style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
                {overlays.map((ov, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: ov.x, top: ov.y, width: ov.w,
                    cursor: 'move', border: '1px dashed rgba(37,99,235,0.6)',
                    touchAction: 'none',
                  }}
                    onPointerDown={(e) => onDragStart(i, e)}>
                    <img src={ov.url} alt="" draggable={false}
                      style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', top: -22, right: 0,
                      display: 'flex', gap: 2 }}>
                      <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setOverlays((o) => o.map((x, j) =>
                          j === i ? { ...x, w: Math.max(50, x.w - 20) } : x))}>−</button>
                      <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setOverlays((o) => o.map((x, j) =>
                          j === i ? { ...x, w: Math.min(600, x.w + 20) } : x))}>+</button>
                      <button style={{ fontSize: '0.65rem', padding: '0 4px' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setOverlays((o) => o.filter((_, j) => j !== i))}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
